/**
 * Trade-in actions, a partner-facing subset of reware-admin's Trade-ins portal: Approve as the
 * primary button, plus a "⋮" menu with Switch payment to gift card, Issue gift card, Email
 * shipping label, Edit note, Edit shipping address, Edit sendback opt-in, and Cancel. All hit
 * the same admin_graphql mutations reware-admin uses.
 *
 * The two sendback actions reware-admin offers are deliberately absent — see the menu array.
 *
 * Four of the seven are gated by a server-derived flag and carry a reason when disabled; the
 * note, address and opt-in edits are always available. Approve additionally requires every
 * active item to be priced and out of review, which the flag covers but the reason cannot
 * explain.
 */
import { useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { isInStore } from './TradeinDetailSections';
import {
  approveTradein,
  cancelTradein,
  emailShippingLabel,
  issueGiftcard,
  saveTradeinNotes,
  switchPaymentToGiftcard,
  updateSendbackOptIn,
  updateShippingAddress,
} from './mutations';
import type { ConstantOption, TradeinRecord } from './types';

interface TradeinActionsProps {
  tradein: TradeinRecord;
  giftcardReasons: ConstantOption[];
}

type DialogKind = null | 'giftcard' | 'note' | 'address' | 'cancel';

export const TradeinActions = ({ tradein, giftcardReasons }: TradeinActionsProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [dialog, setDialog] = useState<DialogKind>(null);

  const [giftcardAmount, setGiftcardAmount] = useState('');
  const [giftcardEmail, setGiftcardEmail] = useState(tradein.deliveryAddress?.email ?? '');
  const [giftcardReason, setGiftcardReason] = useState('');
  const [note, setNote] = useState(tradein.notes ?? '');
  const [address, setAddress] = useState({
    address1: tradein.deliveryAddress?.address1 ?? '',
    address2: tradein.deliveryAddress?.address2 ?? '',
    city: tradein.deliveryAddress?.city ?? '',
    state: tradein.deliveryAddress?.state ?? '',
    postalCode: tradein.deliveryAddress?.postalCode ?? '',
  });

  /**
   * Closes the dialog only on success, so a rejected action leaves the form open with the
   * operator's input intact rather than discarding a typed amount, recipient and reason.
   *
   * One caveat for gift cards: the server creates the card and then emails it, without a
   * transaction. If the email step fails the operator sees an error over a still-populated form
   * and may reasonably retry, issuing a second card. Distinguishing the two failures needs a
   * server change; until then the error text is all that stands between them.
   */
  const run = async (
    { success, failure }: { success: string, failure: string },
    action: () => Promise<unknown>
  ) => {
    setBusy(true);
    setMenuAnchor(null);
    try {
      await action();
      notify(success, { type: 'success' });
      setDialog(null);
      refresh();
    } catch (error) {
      // Separate labels, not `${success} failed` — that yields "Gift card issued failed", which
      // reads as though the card was issued.
      notify(error instanceof Error && error.message ? error.message : failure, { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  // Each gate gets its own reason rather than sharing this one. canIssueGiftcard in particular
  // is a partner gift-card-vendor capability, so telling someone to wait for the state to change
  // would have them wait for something that never happens.
  const stateReason = 'Not available in the trade-in’s current state';
  const approveReason = tradein.canBeApproved ? null : stateReason;

  interface MenuAction {
    key: string;
    label: string;
    reason: string | null;
    onClick: () => void;
    hide?: boolean;
  }

  const menuActions: MenuAction[] = [
    // No "Send items back" or "Pack sendback" here. Both need UI this page does not have, and
    // firing their mutations without it is worse than omitting them — see the notes on
    // initiateSendback and packSendback in mutations.ts. Re-add them with an item picker and a
    // dimensions form respectively.
    { key: 'switchPayment', label: 'Switch payment to gift card', reason: tradein.canSwitchPaymentPreference ? null : 'Only for bank-transfer payouts, before the trade-in completes', onClick: () => run({ success: 'Payment preference updated', failure: 'Couldn’t update the payment preference' }, () => switchPaymentToGiftcard(tradein.id)) },
    { key: 'giftcard', label: 'Issue gift card', reason: tradein.canIssueGiftcard ? null : 'This partner has no gift card vendor configured', onClick: () => { setMenuAnchor(null); setDialog('giftcard'); } },
    { key: 'emailLabel', label: 'Email shipping label', reason: tradein.state === 'PENDING' ? null : 'Only while pending', onClick: () => run({ success: 'Shipping label emailed', failure: 'Couldn’t email the shipping label' }, () => emailShippingLabel(tradein.id)), hide: isInStore(tradein) },
    { key: 'optIn', label: tradein.isSendbackOptIn ? 'Turn off sendback opt-in' : 'Turn on sendback opt-in', reason: null, onClick: () => run({ success: 'Sendback opt-in updated', failure: 'Couldn’t update the sendback opt-in' }, () => updateSendbackOptIn(tradein.id, !tradein.isSendbackOptIn)) },
    { key: 'note', label: 'Edit note', reason: null, onClick: () => { setMenuAnchor(null); setDialog('note'); } },
    { key: 'address', label: 'Edit shipping address', reason: null, onClick: () => { setMenuAnchor(null); setDialog('address'); }, hide: isInStore(tradein) },
    { key: 'cancel', label: 'Cancel trade-in', reason: tradein.canBeCanceled ? null : stateReason, onClick: () => { setMenuAnchor(null); setDialog('cancel'); } },
  ];

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
      {tradein.canBeApproved && (
        <Button
          size="small"
          variant="contained"
          color="success"
          disabled={busy || !!approveReason}
          onClick={() => run({ success: 'Trade-in approved', failure: 'Couldn’t approve the trade-in' }, () => approveTradein(tradein.id))}
        >
          Approve
        </Button>
      )}
      <IconButton
        size="small"
        onClick={event => setMenuAnchor(event.currentTarget)}
        aria-label="Trade-in actions"
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      {/* The reason renders inline rather than as a tooltip. A native `title` never fires on a
          disabled MenuItem — MUI sets pointer-events: none, so the browser dispatches no hover —
          and wrapping each item in a Tooltip + <span> breaks keyboard navigation in a MenuList.
          Inline is the only placement the operator actually sees. */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {menuActions.filter(action => !action.hide).map(action => (
          <MenuItem
            key={action.key}
            disabled={busy || !!action.reason}
            onClick={action.onClick}
            sx={{ display: 'block', py: action.reason ? 0.75 : undefined }}
          >
            <Typography variant="body2">{action.label}</Typography>
            {action.reason && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                {action.reason}
              </Typography>
            )}
          </MenuItem>
        ))}
      </Menu>

      {/* Issue gift card */}
      <Dialog open={dialog === 'giftcard'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Issue gift card</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Amount (USD)" value={giftcardAmount} onChange={e => setGiftcardAmount(e.target.value)} inputProps={{ inputMode: 'decimal' }} />
            <TextField label="Recipient email" value={giftcardEmail} onChange={e => setGiftcardEmail(e.target.value)} />
            <TextField select label="Reason" value={giftcardReason} onChange={e => setGiftcardReason(e.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
              <option value="" disabled>Select a reason</option>
              {giftcardReasons.map(r => <option key={r.code} value={r.code}>{r.displayName}</option>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || !giftcardReason || !giftcardEmail || Number.isNaN(parseFloat(giftcardAmount))}
            onClick={() => run({ success: 'Gift card issued', failure: 'Couldn’t issue the gift card' }, () => issueGiftcard({ tradeinId: tradein.id, amount: parseFloat(giftcardAmount), email: giftcardEmail, reason: giftcardReason }))}
          >
            Issue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit note */}
      <Dialog open={dialog === 'note'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Edit trade-in note</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={3} value={note} onChange={e => setNote(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={() => run({ success: 'Note saved', failure: 'Couldn’t save the note' }, () => saveTradeinNotes(tradein.id, note))}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit shipping address */}
      <Dialog open={dialog === 'address'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Edit shipping address</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Address 1" value={address.address1} onChange={e => setAddress({ ...address, address1: e.target.value })} />
            <TextField label="Address 2" value={address.address2} onChange={e => setAddress({ ...address, address2: e.target.value })} />
            <TextField label="City" value={address.city} onChange={e => setAddress({ ...address, city: e.target.value })} />
            <Stack direction="row" spacing={2}>
              <TextField label="State" value={address.state} onChange={e => setAddress({ ...address, state: e.target.value })} />
              <TextField label="Postal Code" value={address.postalCode} onChange={e => setAddress({ ...address, postalCode: e.target.value })} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={() => run({ success: 'Shipping address updated', failure: 'Couldn’t update the shipping address' }, () => updateShippingAddress({ tradeinId: tradein.id, ...address }))}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel trade-in */}
      <Dialog open={dialog === 'cancel'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Cancel trade-in?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {`This cancels trade-in ${tradein.uuidPrefix ?? `#${tradein.id}`}. This cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Keep trade-in</Button>
          <Button variant="contained" color="error" disabled={busy} onClick={() => run({ success: 'Trade-in canceled', failure: 'Couldn’t cancel the trade-in' }, () => cancelTradein(tradein.id))}>
            Cancel trade-in
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default TradeinActions;
